import React, {useMemo, useState} from 'react';
import {List, Set} from 'immutable';
import {UserComment} from 'records/UserComment';
import {CallTick} from '@components/call-card/CallTick';
import './UserComments.sass';
import {CallComment} from '@components/call-card/CallComment';
import {PlayerProps} from '@components/call-card/Player.utils';
import {NewUserComment} from '@components/call-card/NewUserComment';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {buildCommentBuckets, TRACK_HEIGHT} from '@components/call-card/buildCommentBuckets';
import {SetState} from '@store/@types/util/React';
import {DragStatus} from '@store/call-card/CallCard.types';
import {TrackHighlight} from '@components/call-card/TrackHighlight';
import {UserAvatar} from '@components/util/UserAvatar';
import User, {UserId} from 'records/User';
import {Seconds} from '@store/@types/util';
import {getStringQueryParam} from 'utils/getQueryParam';

interface UserCommentRowProps extends PlayerProps {
  comments: List<UserComment>;
  callId: FileUploadId;
  setNewComment?: SetState<UserComment | undefined>;
  dragStatus?: DragStatus;
  sessionUser: User;
  directReports: Set<UserId>;
  setTimeSeconds: (seconds: Seconds) => void;
  playRecording: () => void;
}

export const UserCommentRow: React.FC<UserCommentRowProps> = ({
  comments,
  callId,
  setNewComment,
  dragStatus,
  sessionUser,
  directReports,
  setTimeSeconds,
  playRecording,
  ...playerProps
}) => {
  const [trackRef, setTrackRef] = useState<HTMLDivElement | null>(null);
  const commentUser = comments.first()?.author ?? sessionUser;
  const isAuthUser = sessionUser?.id === commentUser?.userId;
  const isAdmin = sessionUser?.isAdmin();
  const canDelete = directReports.has(commentUser?.userId) || isAdmin || isAuthUser;
  const canEdit = isAuthUser;

  // Provided by Inbox email links
  const openCommentId = getStringQueryParam(window.location, 'openCommentId');
  const openHighlightId = getStringQueryParam(window.location, 'openHighlightId');

  const {bucketedComments, trackHeight} = useMemo(() => {
    const {bucketedComments, trackHeight} = buildCommentBuckets(comments, TRACK_HEIGHT.DEFAULT);
    return {
      bucketedComments,
      // We add 5px of padding for auth user to give them a clickable area under highlights
      trackHeight: isAuthUser ? trackHeight + 5 : trackHeight,
    };
  }, [comments, isAuthUser]);

  return (
    <div className="user-comments">
      <div className="user">
        <span className="comment-count">({comments.size})</span>
        <UserAvatar user={commentUser} />
      </div>
      <div className="track" ref={r => setTrackRef(r)} style={{height: trackHeight}}>
        {dragStatus && (
          <TrackHighlight
            tickHeight={trackHeight}
            dragStatus={dragStatus}
            duration={playerProps.duration}
            trackWidth={trackRef?.clientWidth}
          />
        )}
        {isAuthUser && (
          <NewUserComment {...playerProps} setNewComment={setNewComment} track={trackRef} />
        )}
        {bucketedComments.map(({index, comment}) => (
          <CallComment
            key={comment.id}
            comment={comment}
            callId={callId}
            bucketIndex={index}
            setEditComment={canEdit ? setNewComment : undefined}
            defaultOpen={comment.id === openCommentId || comment.id === openHighlightId}
            canDelete={canDelete}
            setTimeSeconds={setTimeSeconds}
            playRecording={playRecording}
            {...playerProps}
          />
        ))}
        <CallTick
          duration={playerProps.duration}
          location={playerProps.currentTime}
          type="player-dotted"
        />
      </div>
    </div>
  );
};
